import CloseIcon from '@mui/icons-material/Close';
import { AlertColor, IconButton } from '@mui/material';
import { useSnackbar } from 'notistack';
import { BEEPSOUND } from 'pages/registra-venda/registra-venda';
import { useGenerateUuid } from './useGenerateUuid';

const DEFAULT_SNACK_HIDE_DURATION = 5000;

const SnackbarCloseButton = ({ snackbarKey }: any) => {
  const { closeSnackbar } = useSnackbar();

  return (
    <IconButton onClick={() => closeSnackbar(snackbarKey)} sx={{ minHeight: '50px' }}>
      <CloseIcon sx={{ color: 'white' }} />
    </IconButton>
  );
};

export type CustomSnack = {
  id?: string,
  message: string;
  severity?: AlertColor;
  hideDuration?: number;
  closeable?: boolean;
  persist?: boolean;
  preventDuplicate?: boolean;
};

export const useSnackbarCustom = () => {
  const { closeSnackbar, enqueueSnackbar } = useSnackbar();
  const [randomUuid] = useGenerateUuid();

  const getKey = (id?: string) => {
    if (!id) {
      return randomUuid({ prefix: 'snack-' });
    }
    return id;
  };

  const addSnack = (props: CustomSnack) => {
    const hideDuration = props.hideDuration ?? DEFAULT_SNACK_HIDE_DURATION;

    enqueueSnackbar({
      action: props.closeable ? snackbarKey => <SnackbarCloseButton snackbarKey={snackbarKey} /> : undefined,
      message: props.message,
      key: getKey(props.id),
      variant: props.severity ? props.severity : 'error',
      persist: props.persist,
      autoHideDuration: hideDuration,
      preventDuplicate: props.preventDuplicate ?? true
    });
  };

  return {
    addStack: (snacks: Array<CustomSnack>) => {
      snacks.forEach(s => addSnack({ ...s }));
    },
    addError: (snack: CustomSnack) => {
      addSnack({ ...snack, severity: 'error', });
    },
    addErrorWithSound: (snack: CustomSnack) => {
      BEEPSOUND.play();
      addSnack({ ...snack, severity: 'error', });
    },
    addSuccess: (snack: CustomSnack) => {
      addSnack({ ...snack, severity: 'success', });
    },
    addInfo: (snack: CustomSnack) => {
      addSnack({ ...snack, severity: 'info', });
    },
    addWarning: (snack: CustomSnack) => {
      addSnack({ ...snack, severity: 'warning', });
    },
    addSnack: (snack: CustomSnack) => {
      addSnack(snack);
    },
    closeSnack: (key: string) => {
      closeSnackbar(key);
    }
  };
};
